import { ModalRef, sendNotification, useApiClient, useOptionsetMetadata, useTranslation } from '@msdk/common'
import {
  BaseOptionDefinition,
  DateTimeFieldAccuracy,
  DateTimeValue,
  Entity,
  EntityMetadata,
  EntityModel,
  readBaseMetadataByName,
  readEntityModelIncludeExternal,
  readUserData,
  ServerOperationResult,
  TenantPath,
} from '@msdk/definitions'
import { parseSize } from '@msdk/forms'
import {
  Box,
  ButtonPrimary,
  ButtonSecondary,
  Card,
  CardActions,
  CardBody,
  CardHeader,
  Checkbox,
  COLORS,
  FormControlLabel,
  InfoIcon,
  ModalBase,
  styled,
  Typography,
} from '@msdk/ui'
import { Suspense, useState } from 'react'

import { DocumentContent } from './DocumentContent'

const Container = styled('div')`
  width: 800px;
`

const StyledHeader = styled(CardHeader)`
  border-bottom: 1px solid ${COLORS.divider};
`

const StyledActions = styled(CardActions)`
  border-top: 1px solid ${COLORS.divider};
`

const StyledCardBody = styled(CardBody)`
  max-height: 500px;
  overflow-y: auto;
`

const StyledDiv = styled('div')`
  display: flex;
  gap: 8px;
  align-items: center;
`

const StyledButton = styled(ButtonPrimary)`
  background-color: ${COLORS.error.main};
  &:hover {
    background-color: ${COLORS.error.dark};
    box-shadow: unset;
  }
`

const StyledBox = styled(Box)`
  background-color: ${COLORS.info.light};
  border: 1px solid ${COLORS.info.outlinedBorder};
  border-radius: 8px;
  height: 50px;
  gap: 8px;
  display: flex;
  align-items: center;
  padding: 0 8px;
`

const StyledFormControlLabel = styled(FormControlLabel)`
  .MuiFormControlLabel-label {
    font-weight: bold;
  }
`

const WithdrawConfirmationContainer = styled('div')`
  padding: 16px 24px;
  display: flex;
  flex-direction: column;
  gap: 16px;
`

interface Props {
  entity: Entity
  modalRef?: ModalRef
}

interface WithdrawConfirmationProps {
  isConfirmed: boolean
  onConfirmed: (check: boolean) => void
}

export function WithdrawConfirmation(props: WithdrawConfirmationProps) {
  const t = useTranslation()
  return (
    <WithdrawConfirmationContainer>
      <Typography variant="body1">
        {t(
          'LBL_DOCUMENT_CONFIRM_WITHDRAW_BODY',
          'This will send a request to permanently delete your user account and its data.',
        )}
      </Typography>

      <StyledFormControlLabel
        required
        control={
          <Checkbox
            data-test-id="additional-id-mandatory"
            checked={props.isConfirmed}
            onChange={(e) => props.onConfirmed(e.target.checked)}
          />
        }
        label={t('LBL_DOCUMENT_CONFIRM_WITHDRAW_CHECKBOX', 'I confirm')}
      />
    </WithdrawConfirmationContainer>
  )
}

export function DocumentViewer(props: Props) {
  const t = useTranslation()
  const tenantPath = new TenantPath()
  const client = useApiClient()
  const isDPREnabled = tenantPath.IsMicroServiceEnabled('DPR')
  const docOpt = useOptionsetMetadata('CMPS', 'DOCUMENT_TYPES')
  const file = props.entity.fields['FILE']?.value as string
  const isPrivacyPolicy = props.entity.fields['TYPE']?.value == 'PRV'
  const [openWithdrawModal, setOpenWithdrawModal] = useState(false)
  const [confirmed, setConfirmed] = useState(false)

  const handleConfirmed = (value: boolean) => {
    setConfirmed(value)
  }

  const translateOpt = (value: string) => {
    // eslint-disable-next-line @typescript-eslint/no-shadow
    const opt = docOpt?.options.find((opt) => opt.value === value) as BaseOptionDefinition
    return t(opt?.idml, opt?.description)
  }

  const getDate = (value: DateTimeValue | undefined) => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const acc: any = DateTimeFieldAccuracy[value?.accuracy as any]
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const dateTime = new DateTimeValue(value?.value as any, acc, value?.kind)
    return dateTime.toStringForView()
  }

  const sendNotificationToDPO = async () => {
    try {
      const baseMetatdata = readBaseMetadataByName('USER') as EntityMetadata
      const entityModel = readEntityModelIncludeExternal('USER', 'CMPS') as EntityModel
      const userId = readUserData().user_id
      if (userId) {
        const resGetEntity = await client.entity.getEntity(baseMetatdata.msProvider, userId, entityModel)

        if (resGetEntity.result == ServerOperationResult.Success && resGetEntity.entities.length > 0) {
          const entity = resGetEntity.entities[0]

          if (entity.fields['HAS_WITHDRAWN_CONSENT']?.value !== '1') {
            const setWithdrawEndpoint = 'USM/api/WithdrawConsent/SetWithdrawConsentRequested'
            const setWithdrawBody = { userId }

            const res = await client.post(setWithdrawEndpoint, setWithdrawBody)

            if (res != ServerOperationResult.Success) {
              throw new Error('Failed to send withdraw request: error during user save')
            }

            const notificationEndpoint = 'DPR/api/DPRNotification/SendUserPrivacyConsentWithdrawToDPO'
            const notificationBody = {
              userEmail: readUserData().email,
              userFirstName: readUserData().firstname,
              userLastName: readUserData().lastname,
              tenantId: readUserData().tenant,
            }

            await client.post(notificationEndpoint, notificationBody)

            sendNotification({
              message: t('LBL_DOCUMENT_WITHDRAW_SUCCESS', 'Withdraw request sent successfully'),
              status: 'success',
            })
          } else {
            sendNotification({
              message: t('LBL_DOCUMENT_WITHDRAW_ALREADY_SENT', 'Withdraw request already sent for this user'),
              status: 'info',
            })
          }
        } else {
          throw new Error('Failed to send withdraw request: error during user fetch')
        }
      } else {
        throw new Error('Failed to send withdraw request: user id not present')
      }
    } catch (error) {
      sendNotification({
        message: t('LBL_DOCUMENT_WITHDRAW_ERROR', 'Failed to send withdraw request'),
        status: 'error',
      })
    }

    setOpenWithdrawModal(false)
    setConfirmed(false)
  }

  return (
    <Container>
      <Card>
        <StyledHeader title={translateOpt(props.entity.fields['TYPE']?.value)} />
        <Suspense>
          <StyledCardBody>
            {isDPREnabled && isPrivacyPolicy && (
              <StyledBox>
                <InfoIcon color="info" />
                <Typography variant="body2" color={COLORS.info.main}>
                  {t(
                    'LBL_DOCUMENT_VIEWER_USR_INFO_WITHDRAW',
                    'If you wish to withdraw consent scroll to the bottom of this policy for options',
                  )}
                </Typography>
              </StyledBox>
            )}
            <DocumentContent entityName={props.entity.entityName} entityId={props.entity.id} file={file} />
            {isDPREnabled && isPrivacyPolicy && (
              <ButtonPrimary onClick={() => setOpenWithdrawModal(true)}>
                <StyledDiv>
                  <Typography variant="button">
                    {t('LBL_DOCUMENT_VIEWER_USR_BUTTON_WITHDRAW', 'Withdraw consent to this policy')}
                  </Typography>
                </StyledDiv>
              </ButtonPrimary>
            )}
          </StyledCardBody>
        </Suspense>
        <StyledActions>
          <Typography variant="body1">
            <b>{t('LBL_DOCUMENT_VIEWER_VERSION', 'Version')}:</b>
          </Typography>
          <Typography variant="body1">{props.entity.fields['VERSION']?.value}</Typography>
          {props.entity.fields['USER_ACCEPTED_DATE'] && (
            <>
              <Typography variant="body1">
                <b>{t('LBL_DOCUMENT_VIEWER_DATE_ACCEPTED', 'Date accepted')}:</b>
              </Typography>
              <Typography variant="body1">
                {getDate(props.entity.fields['USER_ACCEPTED_DATE'] as DateTimeValue)}
              </Typography>
            </>
          )}
        </StyledActions>
      </Card>
      <ModalBase
        id="withdrawConfirmation"
        isOpen={openWithdrawModal}
        headerTitle={t('LBL_DOCUMENT_CONFIRM_WITHDRAW_TITLE', 'Withdraw consent for user')}
        onDismiss={() => setOpenWithdrawModal(false)}
        body={<WithdrawConfirmation isConfirmed={confirmed} onConfirmed={handleConfirmed} />}
        width={parseSize('Small:Auto').width}
        footer={
          <>
            <ButtonSecondary
              onClick={() => {
                setOpenWithdrawModal(false)
                setConfirmed(false)
              }}
            >
              <Typography variant="button">{t('CANCEL_BUTTON_LABEL', 'Cancel')}</Typography>
            </ButtonSecondary>

            <StyledButton disabled={!confirmed} onClick={sendNotificationToDPO}>
              <Typography variant="button">{t('LBL_DOCUMENT_CONFIRM_WITHDRAW_BUTTON', 'Withdraw consent')}</Typography>
            </StyledButton>
          </>
        }
      />
    </Container>
  )
}
